import { Department, OrderStatus } from '../../lib/types';
import { createOrder } from '../../lib/api';

function parseCSVRow(row: string): string[] {
  const result: string[] = []; let current = ''; let inQuotes = false;
  for (let i = 0; i < row.length; i++) {
    const ch = row[i];
    if (inQuotes) {
      if (ch === '"' && row[i + 1] === '"') { current += '"'; i++; }
      else if (ch === '"') inQuotes = false;
      else current += ch;
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === ',') { result.push(current.trim()); current = ''; }
      else current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

interface ParsedRow {
  date: string; staff: string; deptId: string; vendor: string;
  category: string; orderNum: string; status: string; notes: string;
  itemName: string; qty: number; price: number; size: string; sku: string;
}

export async function importOrdersCSV(
  file: File,
  departments: Department[],
  categories: string[],
): Promise<{ created: number; failed: number; errors: string[] }> {
  const text = await file.text();
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l && !l.startsWith('#'));
  if (lines.length < 2) {
    return { created: 0, failed: 0, errors: ['CSV has no data rows'] };
  }
  const header = lines[0].toLowerCase();
  const si = header.includes('order date') || header.includes('date') ? 1 : 0;
  const dataLines = lines.slice(si);

  const deptMap: Record<string, string> = {};
  departments.forEach(d => { deptMap[d.name.toLowerCase()] = d.id; });
  const validCats = new Set(categories.map(c => c.toLowerCase()));

  const errors: string[] = [];
  const rows: ParsedRow[] = [];

  dataLines.forEach((line, idx) => {
    const cols = parseCSVRow(line);
    if (cols.length < 9) { errors.push(`Row ${idx + 1}: not enough columns`); return; }
    const [date, staff, dept, vendor, cat, orderNum, status, notes, itemName, qtyStr, priceStr, size, sku] = cols;
    if (!date)      { errors.push(`Row ${idx + 1}: missing date`);           return; }
    if (!dept)      { errors.push(`Row ${idx + 1}: missing department`);     return; }
    if (!cat)       { errors.push(`Row ${idx + 1}: missing category`);       return; }
    if (!itemName)  { errors.push(`Row ${idx + 1}: missing item name`);      return; }
    const deptId = deptMap[dept.toLowerCase()];
    if (!deptId) { errors.push(`Row ${idx + 1}: unknown department "${dept}"`); return; }
    if (!validCats.has(cat.toLowerCase())) { errors.push(`Row ${idx + 1}: unknown category "${cat}"`); return; }
    const matchedCat = categories.find(c => c.toLowerCase() === cat.toLowerCase()) || cat;
    const matchedStatus = ['Pending', 'Ordered', 'Delivered', 'Closed']
      .find(s => s.toLowerCase() === (status || 'pending').toLowerCase()) || 'Pending';
    rows.push({
      date, staff: staff || '', deptId, vendor: vendor || '',
      category: matchedCat, orderNum: orderNum || '', status: matchedStatus,
      notes: notes || '', itemName,
      qty: parseInt(qtyStr) || 1, price: parseFloat(priceStr) || 0,
      size: size || '', sku: sku || '',
    });
  });

  if (errors.length > 0 && rows.length === 0) {
    return { created: 0, failed: 0, errors };
  }

  // group rows by order number (or auto-generated key for rows without one)
  const orderGroups: Map<string, ParsedRow[]> = new Map();
  let autoIdx = 0;
  rows.forEach(row => {
    const key = row.orderNum || `__auto_${autoIdx++}`;
    const group = orderGroups.get(key) || [];
    group.push(row);
    orderGroups.set(key, group);
  });

  let created = 0; let failed = 0;
  for (const groupRows of Array.from(orderGroups.values())) {
    const first = groupRows[0];
    const totalCost = groupRows.reduce((s, r) => s + (r.qty * r.price), 0);
    const payload = {
      order_date: first.date, date_ordered: first.date, staff_name: first.staff,
      department_id: first.deptId,
      department_name: departments.find(d => d.id === first.deptId)?.name || '',
      vendor: first.vendor, vendor_name: first.vendor,
      order_number: first.orderNum || undefined,
      status: first.status as OrderStatus, category: first.category,
      total_cost: Math.round(totalCost * 100) / 100,
      notes: first.notes || undefined, source: 'manual',
      order_items: groupRows.map(r => ({
        id: '', order_id: '', item_name: r.itemName, quantity: r.qty,
        unit_price: r.price, unit_cost: r.price,
        size: r.size || undefined, code: r.sku || undefined, sku: r.sku || undefined,
        category: first.category,
      })),
      items: groupRows.map(r => ({
        item_name: r.itemName, quantity: r.qty, unit_cost: r.price,
        size: r.size || undefined, sku: r.sku || undefined,
      })),
    };
    const result = await createOrder(payload as any);
    if (result.ok) created++; else failed++;
  }

  return { created, failed, errors };
}
